import React, { useEffect } from "react";
import { X, CheckCircle2, ShieldCheck } from "lucide-react";
import { getBenefitAmount } from "../utils/BenefitUtils";

const BenefitSummaryPanel = ({
  open,
  selectedBenefits,
  onClose,
  onViewDetails,
  onBuyPlan,
  buying = false,
}) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape" && !buying) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose, buying]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  const benefits = Array.isArray(selectedBenefits) ? selectedBenefits : [];

  const totalPrice = benefits.reduce(
    (sum, b) => sum + (b?.benefitAmount || 0),
    0
  );

 
  const totalMrp = benefits.reduce(
    (sum, b) => sum + (b?.mrp || b?.originalAmount || b?.benefitAmount || 0),
    0
  );
  const youSave = totalMrp - totalPrice;

  return (
    <div
      className="fixed inset-0 z-[110] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-summary-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={buying ? undefined : onClose}
      />

      {/* Panel */}
      <div className="relative h-full w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden animate-panel-in">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
          <div>
            <h3
              id="plan-summary-title"
              className="text-xl font-extrabold text-[#14131A]"
            >
              Your Selection
            </h3>
            <p className="mt-1 text-sm font-semibold text-[#6D28D9]">
              {benefits.length} benefit{benefits.length === 1 ? "" : "s"} selected
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={buying}
            aria-label="Close"
            className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-[#8A8798] hover:text-[#6D28D9] hover:bg-[#F7F5FB] transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable benefit list */}
        <div className="flex-1 overflow-y-auto px-6 flex flex-col gap-1">
          {benefits.length === 0 && (
            <p className="text-sm text-[#8A8798] italic text-center py-8">
              Nothing selected yet.
            </p>
          )}

          {benefits.map((benefit, i) => {
            const amount = getBenefitAmount ? getBenefitAmount(benefit) : null;
            const price = amount
              ? `${amount.label}${amount.sub || ""}`
              : `₹${benefit?.benefitAmount ?? 0}`;
            return (
              <div
                key={benefit?.id || benefit?.code || i}
                className="flex items-center justify-between gap-3 rounded-xl px-3 py-3 bg-[#FAFAFC]"
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <CheckCircle2
                    size={18}
                    className="text-[#15803D] shrink-0"
                    strokeWidth={2.25}
                  />
                  <span className="text-sm font-semibold text-[#14131A] truncate">
                    {benefit?.name}
                  </span>
                </span>
                <span className="text-sm font-bold text-[#14131A] shrink-0">
                  {price}
                </span>
              </div>
            );
          })}
        </div>

        {/* Tear line — a quiet nod to a physical benefits card stub */}
        <div className="mx-6 my-1 border-t border-dashed border-[#DCD9E5]" aria-hidden="true" />

        {/* Footer: totals + CTA */}
        <div className="px-6 pt-4 pb-6 bg-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-[#5B5866]">
              Total price
            </span>
            <span className="text-lg font-extrabold text-[#14131A]">
              ₹{totalPrice.toLocaleString("en-IN")}
            </span>
          </div>

          {youSave > 0 && (
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-[#5B5866]">
                You save
              </span>
              <span className="text-sm font-bold text-[#15803D]">
                ₹{youSave.toLocaleString("en-IN")}
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={() => onBuyPlan?.(benefits)}
            disabled={benefits.length === 0 || buying}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#4C1D95] text-white text-sm font-semibold hover:bg-[#3B0764] active:scale-[0.99] transition-all duration-150 disabled:opacity-40 disabled:pointer-events-none"
          >
            {buying && (
              <span
                className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin"
                aria-hidden="true"
              />
            )}
            {buying ? "Processing…" : "Continue"}
            {!buying && <span aria-hidden="true">→</span>}
          </button>

          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs font-medium text-[#8A8798]">
            <ShieldCheck size={13} className="text-[#15803D]" />
            Secure & safe payments
          </p>
        </div>
      </div>

      <style>{`
        @keyframes panel-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-panel-in {
          animation: panel-in 260ms ease-out;
        }
      `}</style>
    </div>
  );
};

export default BenefitSummaryPanel;